import React, { useState } from 'react';
import {
    PARAMETER_REGISTRY,
    computeDerivedTelemetry,
    listCompoundingParameters,
    listPolicyParameters
} from '../model/parameterRegistry.js';
import './DataExportPanel.css';

/**
 * Comprehensive Data Export Panel
 * Shows all scenario parameters year-by-year for analysis and export
 */
export default function DataExportPanel({ yearlyResults, config, rigorData, uncertaintyResult }) {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!yearlyResults || yearlyResults.length === 0) return null;

    // Filter to active years
    const activeData = yearlyResults.filter(d => d.year >= config.startYear);

    // Format helpers
    const fmt = (val) => val ? `$${(val / 1e9).toFixed(2)}B` : '-';
    const fmtPct = (val) => val ? `${(val * 100).toFixed(2)}%` : '-';
    const fmtNum = (val) => val ? val.toLocaleString() : '-';

    // Export to CSV
    const exportToCSV = () => {
        const headers = [
            'Year',
            // Revenue
            'Total Revenue', 'Employer Revenue', 'Individual Revenue', 'Self-Employed Revenue', 'Federal Redirect', 'State Redirect', 'Copay Revenue',
            // Spending
            'SAFECare Govt', 'Baseline Govt (CBO)', 'Total NHE (ref)', 'Provider Cost', 'Drug Cost', 'Admin Cost', 'Tuition Support', 'Loan Forgiveness', 'Debt Redemption',
            // Trust Fund
            'Trust Fund Balance', 'Reserve Ratio', 'Net Balance', 'Core Balance', 'LTSS Sub-Account',
            // Debt
            'Federal Debt (Baseline)', 'Federal Debt (SAFECARE)', 'Debt Reduction',
            // Medical Debt
            'Medical Debt (Baseline)', 'Medical Debt (SAFECARE)', 'Medical Debt Eliminated',
            // Health Spending
            'Health Spending % GDP (Baseline)', 'Health Spending % GDP (SAFECARE)',
            // Rates (Sec 206)
            'Employer Rate', 'Individual Rate', 'Self-Employed Rate', 'Rate Adjustment Trigger',
            // Provenance
            'Spending Source'
        ];

        const rows = activeData.map(d => {
            const balance = d.totalRevenue - d.totalSpending;
            const debtReduction = (d.baselineDebtToGDP - d.safecareDebtToGDP) * 100;
            const medDebtEliminated = d.medicalDebtBaseline - d.medicalDebtSafecare;

            return [
                d.year,
                // Revenue (in billions)
                (d.totalRevenue / 1e9).toFixed(2),
                (d.revenueEmployer / 1e9).toFixed(2),
                (d.revenueIndividual / 1e9).toFixed(2),
                (d.revenueSelfEmployed / 1e9).toFixed(2),
                (d.revenueFederalRedirect / 1e9).toFixed(2),
                (d.revenueStateRedirect / 1e9).toFixed(2),
                (d.revenueCopay / 1e9).toFixed(2),
                // Spending (in billions)
                (d.totalSpending / 1e9).toFixed(2),
                d.baselineGovtHealthSpending != null ? (d.baselineGovtHealthSpending / 1e9).toFixed(2) : '',
                d.baselineTotalNHE != null ? (d.baselineTotalNHE / 1e9).toFixed(2) : '',
                (d.costProvider / 1e9).toFixed(2),
                (d.costDrug / 1e9).toFixed(2),
                (d.costAdmin / 1e9).toFixed(2),
                (d.costTuitionSupport / 1e9).toFixed(2),
                ((d.costTuitionSupport || 0) / 1e9).toFixed(2), // Loan forgiveness (if tracked)
                (d.costDebtRedemption / 1e9).toFixed(2),
                // Trust Fund
                (d.trustFundBalance / 1e9).toFixed(2),
                (d.trustFundReserveRatio * 100).toFixed(2),
                (balance / 1e9).toFixed(2),
                d.coreBalance != null ? (d.coreBalance / 1e9).toFixed(2) : '',
                d.ltssSubAccountBalance != null ? (d.ltssSubAccountBalance / 1e9).toFixed(2) : '',
                // Debt
                (d.baselineDebtToGDP * 100).toFixed(2),
                (d.safecareDebtToGDP * 100).toFixed(2),
                debtReduction.toFixed(2),
                // Medical Debt
                (d.medicalDebtBaseline / 1e9).toFixed(2),
                (d.medicalDebtSafecare / 1e9).toFixed(2),
                (medDebtEliminated / 1e9).toFixed(2),
                // Health Spending
                (d.baselineHealthShareGDP * 100).toFixed(2),
                (d.safecareHealthShareGDP * 100).toFixed(2),
                // Rates (Sec 206)
                d.ratesApplied ? (d.ratesApplied.employerRate * 100).toFixed(2) + '%' : '-',
                d.ratesApplied ? (d.ratesApplied.baseIndividualRate * 100).toFixed(2) + '%' : '-',
                d.ratesApplied ? (d.ratesApplied.baseSelfEmployedRate * 100).toFixed(2) + '%' : '-',
                d.rateAdjustmentTrigger ? d.rateAdjustmentTrigger.reason : 'No adjustment',
                d.spendingSource || 'simulator'
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `safecare_scenario_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const exportTelemetryJSON = () => {
        const startYear = config.startYear || 2027;
        const year10 = startYear + 10;

        const payload = {
            exportedAt: new Date().toISOString(),
            config,
            policyParameters: listPolicyParameters(config, { onlyChanged: false }),
            compoundingParameters: listCompoundingParameters(),
            derivedTelemetry: {
                [String(startYear)]: computeDerivedTelemetry({ config, year: startYear }),
                [String(year10)]: computeDerivedTelemetry({ config, year: year10 })
            },
            registry: PARAMETER_REGISTRY,
            backtest: rigorData?.backtest ?? null,
            calibrationReport: rigorData?.calibrationReport ?? null,
            uncertaintyBands: uncertaintyResult ? {
                runCount: uncertaintyResult.runCount,
                sampledKeys: uncertaintyResult.sampledKeys,
                percentileLevels: uncertaintyResult.percentileLevels,
                paramRanges: uncertaintyResult.paramRanges,
                summaryPercentiles: uncertaintyResult.summaryPercentiles,
                percentilesByYear: uncertaintyResult.percentilesByYear,
                runMeta: uncertaintyResult.runMeta
            } : null
        };

        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `safecare_telemetry_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // GENERATE PRINTABLE REPORT
    const generatePrintableReport = () => {
        const timestamp = new Date().toLocaleString();

        // --- 1. Calculate Executive Summary Metrics ---
        const firstYear = activeData[0];
        const lastYear = activeData[activeData.length - 1];

        const totalRevenue = activeData.reduce((sum, d) => sum + d.totalRevenue, 0);
        const totalCost = activeData.reduce((sum, d) => sum + d.totalSpending, 0);
        const avgSurplus = (totalRevenue - totalCost) / activeData.length;

        const startingHealthShare = firstYear.baselineHealthShareGDP;
        const endingHealthShare = lastYear.safecareHealthShareGDP;
        const healthShareReduction = ((startingHealthShare - endingHealthShare) * 100).toFixed(1);

        const debtReduction = ((lastYear.baselineDebtToGDP - lastYear.safecareDebtToGDP) * 100).toFixed(1);
        const medicalDebtEliminated = ((activeData[0].medicalDebtBaseline - lastYear.medicalDebtSafecare) / 1e9).toFixed(1);

        // --- 2. Prepare Policy Configuration List ---
        // We use PARAMETER_REGISTRY to get nice labels, and verify values against config
        const policyParams = listPolicyParameters(config, { onlyChanged: false }).map(p => {
            const regItem = PARAMETER_REGISTRY.find(r => r.key === p.key);
            return {
                label: regItem ? regItem.label : p.key,
                value: typeof p.value === 'number' && p.value < 1 && p.value > -1 ? (p.value * 100).toFixed(1) + '%' : p.value,
                group: regItem ? regItem.group : 'Other'
            };
        });

        // Group policies for display
        const groupedPolicies = policyParams.reduce((acc, item) => {
            if (!acc[item.group]) acc[item.group] = [];
            acc[item.group].push(item);
            return acc;
        }, {});

        // --- 3. Construct HTML ---
        const reportHTML = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>SAFECARE+ Simulation Report</title>
                <style>
                    body { font-family: 'Lato', sans-serif; color: #1e293b; padding: 40px; max-width: 1000px; margin: 0 auto; }
                    h1 { font-size: 24px; color: #0f172a; margin-bottom: 5px; border-bottom: 3px solid #0f172a; padding-bottom: 10px; }
                    h2 { font-size: 18px; color: #334155; margin-top: 30px; border-bottom: 2px solid #e2e8f0; padding-bottom: 5px; }
                    h3 { font-size: 14px; color: #64748b; margin-top: 15px; text-transform: uppercase; letter-spacing: 1px; }
                    .header-meta { font-size: 12px; color: #64748b; margin-bottom: 40px; display: flex; justify-content: space-between; }
                    
                    /* KPI Cards */
                    .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
                    .kpi-card { border: 1px solid #e2e8f0; padding: 15px; border-radius: 6px; background: #f8fafc; }
                    .kpi-label { font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 600; }
                    .kpi-value { font-size: 24px; font-weight: 700; color: #0f172a; margin-top: 5px; }
                    .positive { color: #10b981; }
                    .negative { color: #ef4444; }

                    /* Data Table */
                    table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 15px; }
                    th { text-align: right; padding: 8px; border-bottom: 2px solid #cbd5e1; background: #f1f5f9; color: #475569; }
                    th:first-child { text-align: left; }
                    td { text-align: right; padding: 6px 8px; border-bottom: 1px solid #e2e8f0; }
                    td:first-child { text-align: left; font-weight: 600; }
                    tr:nth-child(even) { background: #f8fafc; }

                    /* Config Grid */
                    .config-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; font-size: 12px; }
                    .config-group { margin-bottom: 15px; }
                    .config-item { display: flex; justify-content: space-between; border-bottom: 1px dotted #cbd5e1; padding: 4px 0; }
                    .config-key { color: #475569; }
                    .config-val { font-weight: 600; color: #0f172a; }

                    /* Assumptions */
                    .assumptions { font-size: 11px; color: #64748b; margin-top: 40px; border-top: 1px solid #e2e8f0; padding-top: 10px; }
                    
                    @media print {
                        body { padding: 0; }
                        button { display: none; }
                        .no-break { page-break-inside: avoid; }
                    }
                </style>
            </head>
            <body>
                <div class="header-meta">
                    <span>Generated by SAFEC+ Policy Simulator</span>
                    <span>${timestamp}</span>
                </div>
                <h1>SAFECARE+ Simulation Report</h1>
                
                <h2>Executive Summary</h2>
                <div class="kpi-grid">
                    <div class="kpi-card">
                        <div class="kpi-label">Avg Annual Surplus</div>
                        <div class="kpi-value ${(avgSurplus >= 0 ? 'positive' : 'negative')}">
                            ${(avgSurplus >= 0 ? '+' : '')}${(avgSurplus / 1e9).toFixed(1)}B
                        </div>
                    </div>
                    <div class="kpi-card">
                         <div class="kpi-label">Debt Reduction (Total)</div>
                        <div class="kpi-value positive">-${debtReduction}% GDP</div>
                    </div>
                    <div class="kpi-card">
                        <div class="kpi-label">Health Share Change</div>
                        <div class="kpi-value positive">-${healthShareReduction} pts</div>
                    </div>
                    <div class="kpi-card">
                        <div class="kpi-label">Med Debt Eliminated</div>
                         <div class="kpi-value positive">$${medicalDebtEliminated}B</div>
                    </div>
                </div>

                <h2>Financial Projections (${firstYear.year} - ${lastYear.year})</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Year</th>
                            <th>Revenue</th>
                            <th>Cost</th>
                            <th>Surplus</th>
                            <th>Trust Fund</th>
                            <th>Reserve %</th>
                            <th>Fed Debt</th>
                            <th>Health % GDP</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${activeData.map(d => `
                            <tr>
                                <td>${d.year}</td>
                                <td>$${(d.totalRevenue / 1e9).toFixed(1)}B</td>
                                <td>$${(d.totalSpending / 1e9).toFixed(1)}B</td>
                                <td class="${(d.totalRevenue - d.totalSpending) >= 0 ? 'positive' : 'negative'}">
                                    ${(d.totalRevenue - d.totalSpending) >= 0 ? '+' : ''}$${((d.totalRevenue - d.totalSpending) / 1e9).toFixed(1)}B
                                </td>
                                <td>$${(d.trustFundBalance / 1e9).toFixed(1)}B</td>
                                <td>${(d.trustFundReserveRatio * 100).toFixed(1)}%</td>
                                <td>${(d.safecareDebtToGDP * 100).toFixed(0)}%</td>
                                <td>${(d.safecareHealthShareGDP * 100).toFixed(1)}%</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>

                <div class="no-break">
                    <h2>Configuration Settings</h2>
                    <div class="config-grid">
                        ${Object.entries(groupedPolicies).map(([group, items]) => `
                            <div class="config-group">
                                <h3>${group}</h3>
                                ${items.map(i => `
                                    <div class="config-item">
                                        <span class="config-key">${i.label}</span>
                                        <span class="config-val">${i.value}</span>
                                    </div>
                                `).join('')}
                            </div>
                        `).join('')}
                    </div>
                </div>

                <div class="assumptions">
                    <strong>Model Assumptions:</strong> 
                    Inflation matched to CBO baseline (~2.3%). 
                    Wage growth baseline ~3.5%. 
                    Transition period modeled over ${activeData.length} years.
                    Values derived from microsimulation of ~330M individuals.
                </div>

                <script>
                    window.onload = function() { window.print(); }
                </script>
            </body>
            </html>
        `;

        const reportWindow = window.open('', '_blank', 'width=1000,height=800');
        reportWindow.document.write(reportHTML);
        reportWindow.document.close();
    };

    return (
        <div className="data-export-panel">
            <div className="data-export-header">
                <div>
                    <h3>📊 Comprehensive Scenario Data</h3>
                    <p>All parameters year-by-year for analysis and export</p>
                </div>
                <div className="data-export-actions">
                    <button type="button" onClick={exportToCSV} className="data-export-btn data-export-btn-csv">
                        📥 Export to CSV
                    </button>
                    <button type="button" onClick={exportTelemetryJSON} className="data-export-btn data-export-btn-json">
                        🧾 Export Telemetry (JSON)
                    </button>
                    <button type="button" onClick={() => setIsExpanded(!isExpanded)} className="data-export-btn data-export-btn-toggle">
                        {isExpanded ? '▼ Collapse' : '▶ Expand'}
                    </button>
                </div>
            </div>

            {/* Expanded Table — scrolls inside box, does not expand page */}
            {isExpanded && (
                <div className="data-export-table-scroll">
                    <table className="data-export-table">
                        <thead>
                            <tr className="table-section-headers">
                                <th colSpan="7" className="th-header-section th-revenue">
                                    💰 REVENUE
                                </th>
                                <th colSpan="9" className="th-header-section th-spending">
                                    💸 SPENDING
                                </th>
                                <th colSpan="3" className="th-header-section th-trust">
                                    🏦 TRUST FUND
                                </th>
                                <th colSpan="3" className="th-header-section th-debt">
                                    📉 FEDERAL DEBT
                                </th>
                                <th colSpan="3" className="th-header-section th-med-debt">
                                    🏥 MEDICAL DEBT
                                </th>
                                <th colSpan="2" className="th-header-section th-health">
                                    📊 HEALTH SPENDING
                                </th>
                                <th colSpan="4" className="th-header-section th-rates">
                                    📈 RATES (SEC 206)
                                </th>
                            </tr>
                            <tr className="table-sub-headers">
                                {/* Revenue */}
                                <th className="th-sub-label text-left">Year</th>
                                <th className="th-sub-label">Total</th>
                                <th className="th-sub-label">Employer</th>
                                <th className="th-sub-label">Individual</th>
                                <th className="th-sub-label">Self-Emp</th>
                                <th className="th-sub-label">Federal</th>
                                <th className="th-sub-label">State</th>
                                <th className="th-sub-label">Copay</th>
                                {/* Spending */}
                                <th className="th-sub-label" title="SAFECare program spending">SAFECare Govt</th>
                                <th className="th-sub-label" title="CBO federal health outlays – comparable to SAFECare">Baseline Govt</th>
                                <th className="th-sub-label" title="Total NHE (all payers) – reference only">Total NHE (ref)</th>
                                <th className="th-sub-label">Provider</th>
                                <th className="th-sub-label">Drug</th>
                                <th className="th-sub-label">Admin</th>
                                <th className="th-sub-label">Tuition</th>
                                <th className="th-sub-label">Loan Forg.</th>
                                <th className="th-sub-label">Debt Red.</th>
                                {/* Trust Fund */}
                                <th className="th-sub-label">Balance</th>
                                <th className="th-sub-label">Reserve %</th>
                                <th className="th-sub-label">Net +/-</th>
                                {/* Federal Debt */}
                                <th className="th-sub-label">Baseline</th>
                                <th className="th-sub-label">SAFECARE</th>
                                <th className="th-sub-label">Reduction</th>
                                {/* Medical Debt */}
                                <th className="th-sub-label">Baseline</th>
                                <th className="th-sub-label">SAFECARE</th>
                                <th className="th-sub-label">Eliminated</th>
                                {/* Health Spending */}
                                <th className="th-sub-label">Base % GDP</th>
                                <th className="th-sub-label">SAFE % GDP</th>
                                {/* Rates */}
                                <th className="th-sub-label">Employer</th>
                                <th className="th-sub-label">Individual</th>
                                <th className="th-sub-label">Self-Emp</th>
                                <th className="th-sub-label text-left">Adjustment</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activeData.map((d, idx) => {
                                const balance = d.totalRevenue - d.totalSpending;
                                const isSurplus = balance >= 0;
                                const debtReduction = d.baselineDebtToGDP - d.safecareDebtToGDP;
                                const medDebtEliminated = d.medicalDebtBaseline - d.medicalDebtSafecare;

                                return (
                                    <tr key={d.year} className="data-row">
                                        {/* Revenue */}
                                        <td className="td-year">{d.year}</td>
                                        <td className="td-value td-revenue-total">{fmt(d.totalRevenue)}</td>
                                        <td className="td-value td-muted">{fmt(d.revenueEmployer)}</td>
                                        <td className="td-value td-muted">{fmt(d.revenueIndividual)}</td>
                                        <td className="td-value td-muted">{fmt(d.revenueSelfEmployed)}</td>
                                        <td className="td-value td-muted">{fmt(d.revenueFederalRedirect)}</td>
                                        <td className="td-value td-muted">{fmt(d.revenueStateRedirect)}</td>
                                        <td className="td-value td-muted">{fmt(d.revenueCopay)}</td>
                                        {/* Spending: Govt comparison + NHE reference */}
                                        <td className="td-value td-spending-total">{fmt(d.totalSpending)}</td>
                                        <td className="td-value td-baseline td-compare">{fmt(d.baselineGovtHealthSpending)}</td>
                                        <td className="td-value td-nhe-ref">{fmt(d.baselineTotalNHE)}</td>
                                        <td className="td-value td-muted">{fmt(d.costProvider)}</td>
                                        <td className="td-value td-muted">{fmt(d.costDrug)}</td>
                                        <td className="td-value td-muted">{fmt(d.costAdmin)}</td>
                                        <td className="td-value td-muted">{fmt(d.costTuitionSupport)}</td>
                                        <td className="td-value td-muted">-</td>
                                        <td className="td-value td-muted">{fmt(d.costDebtRedemption)}</td>
                                        {/* Trust Fund */}
                                        <td className="td-value td-trust-balance">{fmt(d.trustFundBalance)}</td>
                                        <td className="td-value td-trust-reserve">{fmtPct(d.trustFundReserveRatio)}</td>
                                        <td className={`td-value td-net-balance ${isSurplus ? 'is-surplus' : 'is-deficit'}`}>
                                            {isSurplus ? '+' : ''}{fmt(balance)}
                                        </td>
                                        {/* Federal Debt */}
                                        <td className="td-value td-muted">{fmtPct(d.baselineDebtToGDP)}</td>
                                        <td className="td-value td-muted">{fmtPct(d.safecareDebtToGDP)}</td>
                                        <td className="td-value td-positive">{fmtPct(debtReduction)}</td>
                                        {/* Medical Debt */}
                                        <td className="td-value td-muted">{fmt(d.medicalDebtBaseline)}</td>
                                        <td className="td-value td-muted">{fmt(d.medicalDebtSafecare)}</td>
                                        <td className="td-value td-positive">{fmt(medDebtEliminated)}</td>
                                        {/* Health Spending */}
                                        <td className="td-value td-muted">{fmtPct(d.baselineHealthShareGDP)}</td>
                                        <td className="td-value td-positive">{fmtPct(d.safecareHealthShareGDP)}</td>
                                        {/* Rates */}
                                        <td className={`td-value ${d.rateAdjustmentTrigger ? 'td-rate-adjusted' : 'td-rate-normal'}`}>
                                            {d.ratesApplied ? fmtPct(d.ratesApplied.employerRate) : '-'}
                                        </td>
                                        <td className={`td-value ${d.rateAdjustmentTrigger ? 'td-rate-adjusted' : 'td-rate-normal'}`}>
                                            {d.ratesApplied ? fmtPct(d.ratesApplied.baseIndividualRate) : '-'}
                                        </td>
                                        <td className={`td-value ${d.rateAdjustmentTrigger ? 'td-rate-adjusted' : 'td-rate-normal'}`}>
                                            {d.ratesApplied ? fmtPct(d.ratesApplied.baseSelfEmployedRate) : '-'}
                                        </td>
                                        <td className={`td-reason ${d.rateAdjustmentTrigger ? 'has-trigger' : ''}`}>
                                            {d.rateAdjustmentTrigger ? d.rateAdjustmentTrigger.reason : '-'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Summary Stats (Always Visible) */}
            {!isExpanded && (
                <div className="summary-grid">
                    <div className="summary-stat-card">
                        <div className="summary-stat-label">Years Covered</div>
                        <div className="summary-stat-value text-navy">{activeData.length}</div>
                    </div>
                    <div className="summary-stat-card">
                        <div className="summary-stat-label">Avg Annual Revenue</div>
                        <div className="summary-stat-value text-green">
                            {fmt(activeData.reduce((sum, d) => sum + d.totalRevenue, 0) / activeData.length)}
                        </div>
                    </div>
                    <div className="summary-stat-card">
                        <div className="summary-stat-label">Avg Annual Cost</div>
                        <div className="summary-stat-value text-red">
                            {fmt(activeData.reduce((sum, d) => sum + d.totalSpending, 0) / activeData.length)}
                        </div>
                    </div>
                    <div className="summary-stat-card">
                        <div className="summary-stat-label">Avg Annual Surplus</div>
                        <div className="summary-stat-value text-amber">
                            {fmt(activeData.reduce((sum, d) => sum + (d.totalRevenue - d.totalSpending), 0) / activeData.length)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
